/**
 * Global header search — application navigation index.
 *
 * Top-level items come from NAV_SECTIONS filtered by useNavVisibility (feature
 * flags from GET /auth/me/features + role view_actions from GET /auth/me).
 *
 * In-page tabs are derived from SCOPED_FEATURES (policyUtils) and filtered with
 * the same canViewUI checks individual pages use via useTabVisibility.
 */

import type { LucideIcon } from 'lucide-react'
import { Key, ShieldCheck, SlidersHorizontal, UserCheck, Users } from 'lucide-react'
import type { FeatureFlagKey } from '../constants/featureFlags'
import { isAdminPanelTabId } from './adminPanelAccess'
import type { NavItem, NavSection } from '../constants/navigation'
import {
  findScopedFeature,
  getParentTabContainerKeys,
  type SubFeatureMeta,
} from './policyUtils'

export interface GlobalSearchNavItem {
  label: string
  path: string
  icon: LucideIcon
  group: string
}

const DOCS_NAV_PATH = '/resources/docs'

/** Admin in-page tabs — not in SCOPED_FEATURES; gated by tenant flags like AdminPage. */
const ADMIN_SEARCH_TABS: ReadonlyArray<{
  id: string
  label: string
  icon: LucideIcon
  flag?: FeatureFlagKey
}> = [
  { id: 'pending-users', label: 'Pending Users', icon: UserCheck, flag: 'admin_users_view' },
  { id: 'registered-users', label: 'Registered Users', icon: Users, flag: 'admin_users_view' },
  { id: 'manage-roles', label: 'Manage Roles', icon: ShieldCheck, flag: 'admin_roles_view' },
  { id: 'iam', label: 'Cloud IAM', icon: Key },
  { id: 'settings', label: 'General Settings', icon: SlidersHorizontal, flag: 'settings_view' },
]

export { DOCS_NAV_PATH }

function serviceToUrlKey(service: string, tabKey?: string): string {
  return tabKey ?? service.replace(/_/g, '-')
}

/** Sub-features that already have their own sidebar route (e.g. `/benchmarks`). */
function dedicatedSubFeatureIds(sections: NavSection[], featureId: string): Set<string> {
  const ids = new Set<string>()
  for (const item of sections.flatMap((section) => section.items)) {
    if (item.featureId === featureId && item.subFeatureId) ids.add(item.subFeatureId)
  }
  return ids
}

function buildSubFeatureSearchPath(
  basePath: string,
  featureKey: string,
  subKey: string,
  meta: SubFeatureMeta,
  allSubFeatures: Record<string, SubFeatureMeta>,
): string {
  const urlKey = serviceToUrlKey(subKey, meta.tabKey)

  if (featureKey === 'data') {
    return `${basePath}#${urlKey}`
  }

  if (featureKey === 'evals-simulations') {
    if (meta.parentTab) {
      const parentMeta = allSubFeatures[meta.parentTab]
      const parentTab = serviceToUrlKey(meta.parentTab, parentMeta?.tabKey)
      return `${basePath}?tab=${parentTab}&subtab=${urlKey}`
    }
    return `${basePath}?tab=${urlKey}`
  }

  if (featureKey === 'monitoring') {
    if (meta.parentTab === 'token-usage') {
      return `${basePath}?tab=token-usage&sub=${urlKey}`
    }
    if (
      meta.parentTab === 'deployments' ||
      subKey === 'dep_agents' ||
      subKey === 'dep_workflows'
    ) {
      return `${basePath}?tab=deployments`
    }
    return `${basePath}?tab=${urlKey}`
  }

  if (featureKey === 'connections') {
    // Connections sub-features (databases, storage, orchestrators, telephony) are
    // tabs on the Connections page, selected via ?tab=. urlKey already matches the
    // page's tab ids (storage_accounts → tabKey 'storage', etc.).
    return `${basePath}?tab=${urlKey}`
  }

  if (featureKey === 'projects') {
    return basePath
  }

  if (featureKey === 'agent-studio' || featureKey === 'agent-workflows') {
    if (subKey === 'build_with_ai') {
      return `${basePath}/ai-builder`
    }
    if (subKey === 'auto_tune') {
      return `${basePath}/auto-tune`
    }
    if (subKey === 'canvas') {
      return basePath
    }
    return `${basePath}?tab=${urlKey}`
  }

  if (featureKey === 'prompts') {
    if (subKey === 'prompt_optimization') {
      return `${basePath}/optimization`
    }
  }

  if (meta.parentTab) {
    if (featureKey === 'agent-registry' && meta.parentTab === 'guardrails') {
      return `${basePath}?tab=guardrails`
    }
    const parentMeta = allSubFeatures[meta.parentTab]
    const parentTab = serviceToUrlKey(meta.parentTab, parentMeta?.tabKey)
    return `${basePath}?tab=${parentTab}&subtab=${urlKey}`
  }

  return `${basePath}?tab=${urlKey}`
}

function buildScopedSubTabItems(
  navItem: NavItem,
  parentLabel: string,
  sections: NavSection[],
  canViewUI: (feature: string, subFeature?: string) => boolean,
): GlobalSearchNavItem[] {
  const featureId = navItem.featureId
  if (!featureId || navItem.subFeatureId) return []

  const scoped = findScopedFeature(featureId)
  if (!scoped?.subFeatures) return []

  const dedicated = dedicatedSubFeatureIds(sections, featureId)
  const parentContainers = getParentTabContainerKeys(scoped.subFeatures)
  const items: GlobalSearchNavItem[] = []

  for (const [subKey, meta] of Object.entries(scoped.subFeatures)) {
    if (dedicated.has(subKey)) continue
    if (!canViewUI(featureId, subKey)) continue

    // Skip grandchild leaves that share the parent tab URL (registry guardrails, etc.).
    if (meta.parentTab && !parentContainers.has(subKey)) {
      const parentVisible = canViewUI(featureId, meta.parentTab)
      if (!parentVisible) continue
    }

    items.push({
      label: meta.displayName,
      path: buildSubFeatureSearchPath(navItem.path, featureId, subKey, meta, scoped.subFeatures),
      icon: navItem.icon,
      group: parentLabel,
    })
  }

  return items
}

function buildAdminSubTabItems(
  canViewAdmin: boolean,
  isPlatformAdmin: boolean,
  isFlagEnabled: (flag: FeatureFlagKey) => boolean,
  canSeeAdminTab: (tabId: string) => boolean,
): GlobalSearchNavItem[] {
  if (!canViewAdmin) return []

  return ADMIN_SEARCH_TABS.filter((tab) => {
    const flagOk = isPlatformAdmin || !tab.flag || isFlagEnabled(tab.flag)
    const panelOk = isPlatformAdmin || !isAdminPanelTabId(tab.id) || canSeeAdminTab(tab.id)
    return flagOk && panelOk
  }).map((tab) => ({
    label: tab.label,
    path: `/admin?tab=${tab.id}`,
    icon: tab.icon,
    group: 'Admin',
  }))
}

export interface BuildGlobalSearchNavItemsParams {
  sections: NavSection[]
  translateLabel: (labelKey: string) => string
  isItemVisible: (item: NavItem) => boolean
  canViewUI: (feature: string, subFeature?: string) => boolean
  isFlagEnabled: (flag: FeatureFlagKey) => boolean
  isPlatformAdmin: boolean
  canSeeAdminTab?: (tabId: string) => boolean
}

/**
 * Build the searchable application navigation list for the current user.
 * Respects sidebar visibility and per-tab view_actions from auth/me policies.
 */
export function buildGlobalSearchNavItems({
  sections,
  translateLabel,
  isItemVisible,
  canViewUI,
  isFlagEnabled,
  isPlatformAdmin,
  canSeeAdminTab = () => true,
}: BuildGlobalSearchNavItemsParams): GlobalSearchNavItem[] {
  const items: GlobalSearchNavItem[] = []
  const subTabKeys = new Set<string>()
  let adminNavVisible = false

  for (const section of sections) {
    const sectionLabel = section.labelKey ? translateLabel(section.labelKey) : ''

    for (const navItem of section.items) {
      if (!isItemVisible(navItem)) continue

      const label = translateLabel(navItem.labelKey)
      items.push({
        label,
        path: navItem.path,
        icon: navItem.icon,
        group: sectionLabel,
      })

      if (navItem.path === '/admin') {
        adminNavVisible = true
      }

      const subTabs = buildScopedSubTabItems(navItem, label, sections, canViewUI)
      for (const subTab of subTabs) {
        const dedupeKey = `${subTab.path}::${subTab.label}`
        if (subTabKeys.has(dedupeKey)) continue
        subTabKeys.add(dedupeKey)
        items.push(subTab)
      }
    }
  }

  for (const adminTab of buildAdminSubTabItems(
    adminNavVisible,
    isPlatformAdmin,
    isFlagEnabled,
    canSeeAdminTab,
  )) {
    const dedupeKey = `${adminTab.path}::${adminTab.label}`
    if (subTabKeys.has(dedupeKey)) continue
    subTabKeys.add(dedupeKey)
    items.push(adminTab)
  }

  return items
}
